import type { Option } from '~/components/SmartSelect';
import countries, { type CountryIso2 } from '~/constants/countries';
import { currencies, type Currency } from '~/constants/currency';
import { WeightUnit } from '~/constants/weight';
import type { Country } from '~/types/Address';

type JSONCustomsItem = {
  id: number;
  description: string;
  converted_unit_weight: string;
  converted_net_weight: string;
  converted_mass_unit: string;
  value_amount: string | number;
  unit_value: string | number;
  quantity: number;
  value_currency: string;
  tariff_number: Maybe<string>;
  origin_country: string;
  hs_code: Maybe<string>;
  sku_code: Maybe<string>;
  line_item: Maybe<number>;
  eccn_ear99: Maybe<string>;
  original_net_weight: string;
  original_mass_unit: string;
  original_unit_weight: string;
  is_excluded?: boolean;
};

type CustomsItem = {
  id: number;
  description: string;
  convertedUnitWeight: number;
  convertedNetWeight: string;
  convertedMassUnit: WeightUnit;
  valueAmount: string | number;
  unitValue: string | number;
  quantity: number;
  tariffNumber: Maybe<string>;
  originCountry: Country;
  valueCurrency: Maybe<Currency>;
  hsCode: Maybe<string>;
  skuCode: Maybe<string>;
  lineItemId?: number;
  eccnEar99: Maybe<string>;
  originalNetWeight: string;
  originalMassUnit: WeightUnit;
  originalUnitWeight: string;
  isExcluded?: boolean;
};

type CustomsItemPayload = {
  id?: number;
  description: string;
  unitWeight: string;
  massUnit: Option;
  unitValue: string;
  valueCurrency: Option;
  quantity: string;
  originCountry: Country;
  tariffNumber: string;
  skuCode: string;
  lineItemId?: number;
  eccnEar99: string;
  originalNetWeight: string;
  originalMassUnit: Option;
  originalUnitWeight: string;
  isExcluded?: boolean;
};

type JSONCustomsItemPayload = {
  id?: number;
  description: string;
  unit_weight: string;
  mass_unit: string;
  unit_value: string;
  value_currency: string;
  quantity: number;
  origin_country: string;
  tariff_number: string;
  line_item: Maybe<number>;
  sku_code: string;
  eccn_ear99: string;
  original_net_weight: string;
  original_mass_unit: string;
  original_unit_weight: string;
  is_excluded?: boolean;
};

type JSONBatchEntriesCustomsItems = {
  batch_entries: [
    {
      id: number;
      customs_items: JSONCustomsItem[];
    },
  ];
};

export type WebCustomsItem = {
  /** export control classification number (ECCN/EAR99) for US exports */
  control_classification: string;
  description: string;
  is_test: boolean;
  is_excluded: boolean;
  /** @format uuid */
  line_item_object_id: string;
  mass_unit: WeightUnit;
  metadata: string;
  net_value: string;
  net_weight: string;
  /** @format uuid */
  object_id: string;
  /**
   * ISO 3166-2 alpha-2 country code
   * @example "US"
   */
  origin_country: CountryIso2;
  object_state: 'VALID' | 'INVALID';
  quantity: number;
  sku_code: string;
  /** The item's tariff number, e.g. Harmonized System (HS) Codes */
  tariff_number: string;
  unit_value: string;
  unit_weight: string;
  /**
   * ISO 4217 3-letter currency code
   * @example "USD"
   */
  value_currency: string;
};

function customsItemToPayload(customsItem: CustomsItem): CustomsItemPayload {
  // Note: massUnit and valueCurrency do not have a direct conversion; we're also not evaluating these two fields in isEqualToPayload()
  return {
    id: customsItem.id,
    description: customsItem.description,
    unitWeight: customsItem.convertedUnitWeight.toString(),
    massUnit: { label: customsItem.convertedMassUnit, value: customsItem.convertedMassUnit },
    unitValue: customsItem.unitValue.toString(),
    valueCurrency: {
      label: customsItem.valueCurrency?.iso || '',
      value: customsItem.valueCurrency?.iso || '',
    },
    quantity: customsItem.quantity.toString(),
    originCountry: customsItem.originCountry,
    tariffNumber: customsItem.tariffNumber || '',
    skuCode: customsItem.skuCode || '',
    lineItemId: customsItem.lineItemId,
    eccnEar99: customsItem.eccnEar99 || '',
    originalNetWeight: customsItem.originalNetWeight,
    originalUnitWeight: customsItem.originalUnitWeight,
    originalMassUnit: { label: customsItem.originalMassUnit, value: customsItem.originalMassUnit },
    isExcluded: customsItem.isExcluded,
  };
}

function payloadToJSON(customsItemPayload: CustomsItemPayload): JSONCustomsItemPayload {
  return {
    id: customsItemPayload.id,
    description: customsItemPayload.description,
    unit_weight: customsItemPayload.originalUnitWeight.toString(),
    mass_unit: customsItemPayload.originalMassUnit.value as string,
    unit_value: customsItemPayload.unitValue.toString().trim(),
    value_currency: customsItemPayload.valueCurrency.value as string,
    quantity: Number(customsItemPayload.quantity.toString().trim()),
    origin_country: customsItemPayload.originCountry.iso2,
    tariff_number: customsItemPayload.tariffNumber,
    line_item: customsItemPayload.lineItemId ? customsItemPayload.lineItemId : null,
    sku_code: customsItemPayload.skuCode,
    // eccnEar99 must always be included in the payload due to quirky Golang deserializer behavior -
    // eccnEar99 has a custom validator so it's treated as a required field even though it's not marked as such
    eccn_ear99: customsItemPayload.eccnEar99 || '',
    original_mass_unit: customsItemPayload.originalMassUnit.value as string,
    original_unit_weight: customsItemPayload.originalUnitWeight.toString(),
    original_net_weight: customsItemPayload.originalNetWeight?.toString(),
    is_excluded: customsItemPayload.isExcluded,
  };
}

function fromJSON(jsonCustomsItem: JSONCustomsItem): CustomsItem {
  const {
    id,
    description,
    converted_net_weight: net_weight,
    converted_mass_unit: mass_unit,
    value_amount,
    quantity,
    value_currency,
    tariff_number,
    origin_country,
    hs_code,
    sku_code,
    line_item,
    converted_unit_weight: unit_weight,
    unit_value,
    eccn_ear99,
    original_mass_unit,
    original_net_weight,
    original_unit_weight,
    is_excluded,
  } = jsonCustomsItem;

  return {
    id,
    description,
    quantity,
    convertedUnitWeight: Number(unit_weight),
    unitValue: Number(unit_value),
    convertedNetWeight: net_weight,
    convertedMassUnit: <WeightUnit>mass_unit,
    valueAmount: value_amount,
    valueCurrency:
      Object.values(currencies).find(
        (currency) => currency.iso === value_currency?.toUpperCase(),
      ) || null,
    tariffNumber: tariff_number,
    originCountry: countries[origin_country.toUpperCase()],
    hsCode: hs_code,
    skuCode: sku_code,
    lineItemId: line_item ? line_item : undefined,
    eccnEar99: eccn_ear99,
    originalMassUnit: <WeightUnit>original_mass_unit,
    originalNetWeight: original_net_weight,
    originalUnitWeight: original_unit_weight,
    isExcluded: is_excluded,
  };
}

export const fromWebCustomsItem = (webCustomsItem: WebCustomsItem): CustomsItem => {
  const currency = currencies[webCustomsItem.value_currency.toUpperCase()] || null;
  const country = countries[webCustomsItem.origin_country.toUpperCase()];

  return {
    description: webCustomsItem.description,
    valueAmount: webCustomsItem.net_value,
    valueCurrency: currency,
    unitValue: webCustomsItem.unit_value,
    quantity: webCustomsItem.quantity,
    tariffNumber: webCustomsItem.tariff_number,
    originCountry: country,
    skuCode: webCustomsItem.sku_code,
    eccnEar99: webCustomsItem.control_classification,
    originalNetWeight: webCustomsItem.net_weight,
    originalUnitWeight: webCustomsItem.unit_weight,
    originalMassUnit: <WeightUnit>webCustomsItem.mass_unit,
    isExcluded: webCustomsItem.is_excluded,

    // nulls
    id: -1,
    convertedUnitWeight: 0,
    convertedNetWeight: '',
    convertedMassUnit: WeightUnit.LB,
    lineItemId: undefined,
    hsCode: '',
  };
};

function isValid(customsItem: CustomsItem): boolean {
  return [
    customsItem.description,
    customsItem.convertedNetWeight,
    customsItem.convertedMassUnit,
    customsItem.valueAmount,
    customsItem.quantity,
    customsItem.valueCurrency,
  ].every(Boolean);
}

function isEqualToPayload(a: CustomsItem, b: CustomsItemPayload): boolean {
  return (
    (a.lineItemId && b.lineItemId && a.lineItemId === b.lineItemId) ||
    (a.description === b.description &&
      Number(a.convertedUnitWeight) === Number(b.unitWeight) &&
      Number(a.unitValue) === Number(b.unitValue) &&
      Number(a.quantity) === Number(b.quantity) &&
      a.valueCurrency?.iso === b.valueCurrency.value &&
      a.tariffNumber === b.tariffNumber &&
      a.originCountry.iso2 === b.originCountry.iso2 &&
      (a.eccnEar99 === b.eccnEar99 || (!a.eccnEar99 && !b.eccnEar99)))
  );
}

export {
  CustomsItemPayload,
  JSONCustomsItemPayload,
  payloadToJSON,
  JSONCustomsItem,
  CustomsItem,
  fromJSON,
  isValid,
  isEqualToPayload,
  customsItemToPayload,
  JSONBatchEntriesCustomsItems,
};
export default CustomsItem;
